import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  MapPin, 
  Tags, 
  Building, 
  Navigation, 
  CheckCircle2, 
  XCircle,
  Plus,
  Pencil
} from 'lucide-react';
import AlternativeNamesPopup from '../ivr/AlternativeNamesPopup';
import LandmarksPopup from './LandmarksPopup';
import RoadsPopup from './RoadsPopup';
import AddressEditPopup from './AddressEditPopup';

const LocationDetailsPanel = ({ 
  location, 
  expanded,
  onToggleExpand,
  supabase,
  onUpdate 
}) => {
  const [isAlternativeNamesPopupOpen, setIsAlternativeNamesPopupOpen] = useState(false);
  const [isLandmarksPopupOpen, setIsLandmarksPopupOpen] = useState(false);
  const [isRoadsPopupOpen, setIsRoadsPopupOpen] = useState(false);
  const [isAddressEditPopupOpen, setIsAddressEditPopupOpen] = useState(false);
  
  // Track if initial animation has played
  const animatedRef = useRef({});
  
  // Calculate counts
  const alternativeNamesCount = location.alternative_names?.length || 0;
  const landmarksCount = location.landmarks?.length || 0;
  const roadsCount = location.roads?.length || 0;
  
  // Check if location has Google verification
  const hasGoogleVerification = !!(location.google_places_id && location.google_address);
  
  // Feature button variant
  const FeatureButton = ({ icon: Icon, label, count, onClick }) => {
    const hasItems = count > 0;
    const buttonKey = `${label}-${location.id}`;
    const hasAnimated = animatedRef.current[buttonKey];
    
    // Mark this button as animated after first render
    useEffect(() => {
      if (hasItems && !hasAnimated) {
        animatedRef.current[buttonKey] = true;
      }
    }, [hasItems, buttonKey]);
    
    return (
      <motion.button
        onClick={onClick}
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        transition={{ type: "tween", duration: 0.15 }}
        className={`
          relative overflow-hidden group
          flex flex-col items-center justify-center p-3 sm:p-4
          rounded-xl
          ${hasItems 
            ? 'bg-gradient-to-br from-gray-50 to-white border border-gray-100' 
            : 'bg-white border border-dashed border-gray-200'}
        `}
      >
        <div className="flex flex-col items-center">
          <div className={`relative mb-1.5 sm:mb-2 ${hasItems ? 'text-black' : 'text-gray-400'}`}>
            <Icon size={18} className="sm:w-5 sm:h-5" />
            {hasItems && (
              <motion.div
                initial={hasAnimated ? { scale: 1 } : { scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", damping: 20, stiffness: 400 }}
                className="absolute -top-2 -right-2 w-3.5 h-3.5 sm:w-4 sm:h-4 rounded-full bg-black text-white
                  flex items-center justify-center text-[9px] sm:text-[10px] font-medium"
              >
                {count}
              </motion.div>
            )}
          </div>
          <span className={`text-[10px] sm:text-xs font-medium ${hasItems ? 'text-gray-800' : 'text-gray-500'}`}>
            {label}
          </span>
        </div>
        
        {/* Add button indicator for empty states */}
        {!hasItems && (
          <motion.div 
            className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 
              flex items-center justify-center"
            initial={{ opacity: 0 }}
            whileHover={{ opacity: 1 }}
            transition={{ duration: 0.15 }}
          >
            <Plus size={14} className="sm:w-4 sm:h-4 text-gray-500" />
          </motion.div>
        )}
      </motion.button>
    );
  };
  
  // Animation variants - refined for elegance
  const panelVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        when: "beforeChildren",
        staggerChildren: 0.08,
        ease: "easeOut",
        duration: 0.25
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 5 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.25,
        ease: "easeOut"
      }
    }
  };
  
  // Animation for address container
  const addressVariants = {
    hidden: { opacity: 0, y: -5 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.25,
        ease: "easeOut",
        delay: 0.1
      }
    }
  };
  
  return (
    <>
      <motion.div 
        className="w-full"
        initial="hidden"
        animate="visible"
        variants={panelVariants}
      >
        {/* Google verification status */}
        <motion.div 
          variants={itemVariants}
          className="mb-5 sm:mb-6"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center text-xs sm:text-sm">
              <MapPin size={14} className="sm:w-4 sm:h-4 mr-1.5 sm:mr-2 text-gray-500" />
              <span className="font-medium text-gray-800">Location data</span>
            </div>
            
            <motion.div 
              className="flex items-center"
              variants={itemVariants}
            >
              {hasGoogleVerification ? (
                <span className="flex items-center text-emerald-600 text-[10px] sm:text-xs font-medium">
                  <CheckCircle2 size={12} className="sm:w-3.5 sm:h-3.5 mr-1" />
                  Google verified
                </span>
              ) : (
                <span className="flex items-center text-amber-600 text-[10px] sm:text-xs font-medium">
                  <XCircle size={12} className="sm:w-3.5 sm:h-3.5 mr-1" />
                  Unverified
                </span>
              )}
            </motion.div>
          </div>
          
          {/* Address display with edit button */}
          <div className="relative">
            {location.google_address ? (
              <motion.div
                variants={addressVariants}
                className="mt-2 p-2.5 sm:p-3 bg-gray-50 rounded-lg
                  text-xs sm:text-sm text-gray-700 font-light overflow-hidden pr-9"
              >
                {location.google_address}
                
                <motion.button
                  onClick={() => setIsAddressEditPopupOpen(true)}
                  whileHover={{ scale: 1.1, backgroundColor: '#f0f9ff' }}
                  whileTap={{ scale: 0.9 }}
                  className="absolute right-2 top-2 text-gray-400 hover:text-blue-500 rounded-full w-6 h-6
                    flex items-center justify-center hover:bg-blue-50"
                  aria-label="Edit address"
                >
                  <Pencil size={12} className="sm:w-3.5 sm:h-3.5" />
                </motion.button>
              </motion.div>
            ) : (
              <motion.button
                onClick={() => setIsAddressEditPopupOpen(true)}
                variants={addressVariants}
                className="mt-2 p-2.5 sm:p-3 w-full bg-white border border-dashed border-gray-200 rounded-lg
                  text-xs sm:text-sm text-gray-500 font-light flex items-center justify-center 
                  hover:bg-gray-50 transition-colors"
              >
                <Plus size={12} className="mr-1.5 text-gray-400" />
                Add address
              </motion.button>
            )}
          </div>
        </motion.div>
        
        {/* Features grid */}
        <motion.div 
          variants={itemVariants}
          className="grid grid-cols-3 gap-2 sm:gap-3"
        >
          <FeatureButton 
            icon={Tags} 
            label="Alt Names" 
            count={alternativeNamesCount}
            onClick={() => setIsAlternativeNamesPopupOpen(true)}
          />
          
          <FeatureButton 
            icon={Building} 
            label="Landmarks" 
            count={landmarksCount}
            onClick={() => setIsLandmarksPopupOpen(true)}
          />
          
          <FeatureButton 
            icon={Navigation} 
            label="Roads" 
            count={roadsCount}
            onClick={() => setIsRoadsPopupOpen(true)}
          />
        </motion.div>
      </motion.div>
      
      {/* Popups */}
      {isAlternativeNamesPopupOpen && (
        <AlternativeNamesPopup
          location={location}
          onClose={() => setIsAlternativeNamesPopupOpen(false)}
          supabase={supabase}
          onSuccess={onUpdate}
        />
      )}
      
      {isLandmarksPopupOpen && (
        <LandmarksPopup
          location={location}
          onClose={() => setIsLandmarksPopupOpen(false)}
          supabase={supabase}
          onSuccess={onUpdate}
        />
      )}
      
      {isRoadsPopupOpen && (
        <RoadsPopup
          location={location}
          onClose={() => setIsRoadsPopupOpen(false)}
          supabase={supabase}
          onSuccess={onUpdate}
        />
      )}
      
      {isAddressEditPopupOpen && (
        <AddressEditPopup
          location={location}
          onClose={() => setIsAddressEditPopupOpen(false)}
          supabase={supabase}
          onSuccess={onUpdate}
        />
      )}
    </>
  );
};

export default LocationDetailsPanel; 